import React from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import { ReferenceMediaPicker } from './ReferenceMediaPicker';

const AUDIO_FIELDS = [
    { key: 'sfx', label: 'SFX (Sound Effects)', placeholder: 'e.g., thunder, footsteps, door creaking...' },
    { key: 'bgm', label: 'BGM (Background Music)', placeholder: 'e.g., Gentle acoustic guitar, uplifting melody...' },
    { key: 'vfx', label: 'VFX (Visual Effects)', placeholder: 'e.g., explosions, sparkles, lens flare...' },
    { key: 'ambient', label: 'Ambient Sound', placeholder: 'e.g., City traffic, forest birds, ocean waves...' },
];

const ActionRow = ({ action, index, onUpdate, onRemove }) => (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-gray-800/60 border border-gray-700" data-testid={`cpe-action-row-${index}`}>
        <div className="flex flex-col gap-1 shrink-0">
            <span className="text-[10px] uppercase tracking-wider text-gray-500">Start</span>
            <input type="text" value={action.startTime} onChange={e => onUpdate(action.id, { startTime: e.target.value })}
                className="w-20 p-2 bg-gray-700/60 border border-gray-600 rounded-md text-sm" placeholder="0.0s" data-testid={`cpe-start-${index}`} />
            <span className="text-[10px] uppercase tracking-wider text-gray-500 mt-1">End</span>
            <input type="text" value={action.endTime} onChange={e => onUpdate(action.id, { endTime: e.target.value })}
                className="w-20 p-2 bg-gray-700/60 border border-gray-600 rounded-md text-sm" placeholder="4.0s" data-testid={`cpe-end-${index}`} />
        </div>
        <div className="flex-1 min-w-0">
            {action.source === 'scene' && <span className="text-[10px] uppercase tracking-wider text-emerald-300">Scene action</span>}
            <textarea value={action.action} onChange={e => onUpdate(action.id, { action: e.target.value })} rows={5}
                className="w-full p-2 bg-gray-700/60 border border-gray-600 rounded-md text-sm resize-y min-h-[110px]"
                placeholder={`Action ${index + 1}...`} data-testid={`cpe-action-${index}`} />
        </div>
        <button type="button" onClick={() => onRemove(action.id)} className="p-2 text-red-400 hover:bg-red-500/20 rounded-full shrink-0 transition-colors"
            title="Delete action" data-testid={`cpe-remove-${index}`}><Trash2 className="w-4 h-4" /></button>
    </div>
);

export const CinematicPromptEditorModal = ({
    timedActions, onUpdateAction, onRemoveAction, onAddAction,
    audioValues, onAudioChange, audioReferences, onReferenceChange, onClose, disabled,
}) => (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[60] p-4" onClick={onClose} data-testid="cinematic-prompt-editor-overlay">
        <div className="bg-[#14141c] border border-gray-700 rounded-2xl w-full max-w-6xl h-[92vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-testid="cinematic-prompt-editor">
            <header className="px-5 py-4 flex justify-between items-center border-b border-gray-700 shrink-0">
                <div>
                    <h3 className="text-lg font-bold text-white">Edit Cinematic Prompt &amp; Sound</h3>
                    <p className="text-xs text-gray-400">Changes apply to this shot right away.</p>
                </div>
                <button type="button" onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-700" data-testid="cinematic-prompt-editor-close"><X className="w-5 h-5" /></button>
            </header>
            <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
                <section className="flex-1 lg:flex-[3] p-5 overflow-y-auto space-y-3 border-b lg:border-b-0 lg:border-r border-gray-700">
                    <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold text-purple-300">Cinematic Actions <span className="text-gray-500 font-normal">({timedActions.length})</span></h4>
                    </div>
                    {timedActions.map((a, i) => (
                        <ActionRow key={a.id} action={a} index={i} onUpdate={onUpdateAction} onRemove={onRemoveAction} />
                    ))}
                    <button type="button" onClick={onAddAction} className="w-full text-sm bg-indigo-600/50 hover:bg-indigo-600 text-white font-semibold py-2 rounded-md flex items-center justify-center gap-1 transition-colors" data-testid="cpe-add-action">
                        <Plus className="w-4 h-4" /> Add Action
                    </button>
                </section>
                <section className="flex-1 lg:flex-[2] p-5 overflow-y-auto space-y-4">
                    <h4 className="text-sm font-semibold text-purple-300">Sound &amp; Effects</h4>
                    {AUDIO_FIELDS.map(f => (
                        <div key={f.key} className="p-3 rounded-lg bg-gray-800/60 border border-gray-700" data-testid={`cpe-audio-${f.key}`}>
                            <label className="text-xs font-medium text-gray-300">{f.label}</label>
                            <textarea value={audioValues[f.key]} onChange={e => onAudioChange(f.key, e.target.value)} rows={3}
                                placeholder={f.placeholder} className="w-full mt-1 p-2 bg-gray-700/60 border border-gray-600 rounded-md text-sm resize-y"
                                data-testid={`cpe-audio-text-${f.key}`} />
                            <ReferenceMediaPicker category={f.key} label={f.label} value={audioReferences?.[f.key]}
                                onChange={ref => onReferenceChange(f.key, ref)} disabled={disabled} />
                        </div>
                    ))}
                </section>
            </div>
            <footer className="px-5 py-3 border-t border-gray-700 flex justify-end shrink-0">
                <button type="button" onClick={onClose} className="px-5 py-2 bg-purple-600 hover:bg-purple-500 text-white text-sm font-semibold rounded-lg transition-colors" data-testid="cinematic-prompt-editor-done">Done</button>
            </footer>
        </div>
    </div>
);
